import React, { useCallback, useMemo, useState } from "react";

import { Breakpoints, CTA, Divider, Spinner, useIsMobile } from "@vw-marketing/us-components";

import { CommunicationCenterErrorView } from "./components/CommunicationCenterErrorView/CommunicationCenterErrorView";
import { StyledActionCTAsContainer } from "./styles";
import { TopicItem } from "./components/TopicItem/TopicItem";
import { useCommunicationCenter } from "../../app/providers/CommunicationCenterContext";
import { useCommunicationCenterForm } from "./hooks/useCommunicationCenterForm";
import { useMyVwContext } from "../../shared/hooks/use-my-vw-context";
import { useFeatureAppConfig } from "../../shared/hooks/use-feature-app-config";
import PageContainer from "../../shared/components/page-container";
import TabsContent from "../../shared/components/tabs-content";
import { DisclaimerModal } from "../../shared/components/disclaimer-modal";

export const CommunicationCenter = () => {
  // Text -> reference number assigned by the shared disclaimer-manager,
  // populated as each TopicItem registers its disclaimer(s). Keyed by text
  // (rather than purposeId/field) so the dedup behavior of the underlying
  // manager — identical text reuses the same number — is reflected here too.
  const [registeredDisclaimers, setRegisteredDisclaimers] = useState<
    Record<string, string>
  >({});

  const isMobile = useIsMobile(Breakpoints.b560);

  const { isLoading, error, retryFetch } = useCommunicationCenter();
  const { translationHandler } = useMyVwContext();
  const { disclaimers: disclaimerConfig } = useFeatureAppConfig();
  const { tabs, toggleChannel, handleCancel, handleSave, isDirty, isSaving } =
    useCommunicationCenterForm();

  const handleDisclaimerRegistered = useCallback(
    (disclaimerText: string, disclaimerNumber: string) => {
      setRegisteredDisclaimers((current) =>
        current[disclaimerText] === disclaimerNumber
          ? current
          : { ...current, [disclaimerText]: disclaimerNumber },
      );
    },
    [],
  );

  // Adapts the shared disclaimer-manager's (text -> number) registrations
  // into the Disclaimer[] shape the existing local DisclaimerModal expects,
  // sorted numerically so the footer list reads in the same order as the
  // superscript markers on the page.
  const disclaimersForModal = useMemo(
    () =>
      Object.entries(registeredDisclaimers)
        .map(([description, number]) => ({
          number,
          description,
          token: `COMMUNICATION_CENTER_DISCLAIMER_${number}`,
        }))
        .sort((a, b) => Number(a.number) - Number(b.number)),
    [registeredDisclaimers],
  );

  const items = tabs.map((tab) => ({
    key: tab.key,
    labelKey: tab.label,
    content: (
      <>
        {tab.purposes.map((purpose) => {
          const purposeDisclaimers = disclaimerConfig?.[purpose.purposeId];

          return (
            <React.Fragment key={purpose.purposeId}>
              <TopicItem
                channels={purpose.channels}
                topicDescription={purpose.description}
                topicName={purpose.name}
                onChannelToggle={(channelName) => toggleChannel(purpose.purposeId, channelName)}
                nameDisclaimerText={purposeDisclaimers?.nameDisclaimerText}
                descriptionDisclaimerText={purposeDisclaimers?.descriptionDisclaimerText}
                onDisclaimerRegistered={handleDisclaimerRegistered}
              />
              <Divider />
            </React.Fragment>
          );
        })}
      </>
    ),
  }));

  // Disabled until the user has made an actual, net change — reverting a
  // toggle back to its original state (e.g. check then uncheck the same
  // channel) re-disables both CTAs, since isDirty is a deep comparison
  // against the last-saved baseline rather than a "was anything touched" flag.
  const areCTAsDisabled = !isDirty || isSaving;

  return (
    <PageContainer
      descriptionKey={error || isLoading ? undefined : "communicationCenter.description"}
      labelKey={error || isLoading ? undefined : "communicationCenter"}
    >
      {isLoading && (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            height: '60svh',
            justifyContent: 'center',
            paddingBottom: 50,
            width: '100%',
          }}
        >
          <Spinner variant="large" />
        </div>
      )}
      {error && !isLoading && (
        <CommunicationCenterErrorView
          onRetry={retryFetch}
        />
      )}
      {!isLoading && !error && (
        <>
          <TabsContent items={items} />
          <StyledActionCTAsContainer>
            <CTA
              emphasis="secondary"
              stretchContent={isMobile}
              tag="button"
              type="button"
              onClick={handleCancel}
              disabled={areCTAsDisabled}
            >
              {translationHandler('communicationCenter.cancel')}
            </CTA>
            <CTA
              emphasis="primary"
              stretchContent={isMobile}
              tag="button"
              type="button"
              onClick={handleSave}
              disabled={areCTAsDisabled}
            >
              {translationHandler('communicationCenter.save')}
            </CTA>
          </StyledActionCTAsContainer>
        </>
      )}
      <DisclaimerModal disclaimers={disclaimersForModal} />
    </PageContainer>
  );
};
